import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import AppShell from "../components/AppShell";
import EditCompanyModal from "../components/EditCompanyModal";
import { RAISE_REASONS } from "../lib/raiseReasons";
import { VentureSwitcher } from "../components/founder/VentureSwitcher";
import { ArrowRight, FileText, Sparkles, Eye, Pencil, Users, Crown, Share2, Trophy, X, Loader2, CalendarClock, Check } from "lucide-react";
import { LaunchPrepButton } from "../components/LaunchPrepButton";
import CapitalStructurePanel from "../components/founder/CapitalStructurePanel";
import BenchmarkPanel from "../components/founder/BenchmarkPanel";
import FinancialCoveragePanel from "../components/founder/FinancialCoveragePanel";
import PitchDeckCoveragePanel from "../components/founder/PitchDeckCoveragePanel";
import { ConfidenceRibbon } from "../components/founder/WebReportSections";
import ShareScoreModal from "../components/ShareScoreModal";
import { maybeStartFounderTourOnFirstVisit, startDavidWalkthrough } from "../lib/tour";
import { DavidWelcomePopup } from "../components/founder/DavidWelcomePopup";
import { DAVID_ENABLED } from "../lib/features";
import { useAuth } from "../contexts/AuthContext";
import { StudioIntrosCard } from "../components/studio/StudioIntrosCard";
import { CapitalPurposePrompt } from "../components/founder/CapitalPurposePrompt";
import { FiguresPanel } from "../components/founder/FiguresPanel";
import { CAPITAL_PURPOSE_LABEL } from "../lib/quizFunderPreview";
import { sortRoadmap } from "../lib/roadmapOrder";
import { FirstVisitDocsChecklist } from "../components/founder/FirstVisitDocsChecklist";
import { UploadFailedBadge } from "../components/founder/UploadFailedBadge";

const NAV = [
  {to:"/founder/dashboard",label:"Dashboard"},
  {to:"/founder/tutorial",label:"Tutorial"},
  {to:"/founder/team",label:"Team"},
  {to:"/onboarding",label:"Onboarding"},
];

/* ------------------------------------------------------------------ *
 * Horizontal 5-stage progress stepper — mirrors the Capital Journey
 * timeline styling (teal done / amber "You are here"), laid out
 * horizontally right under the dashboard header.
 * ------------------------------------------------------------------ */
function ProgressStepper() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/founder/progress-stepper").then(({ data }) => setData(data)).catch(() => {});
  }, []);
  if (!data) return null;
  const cur = data.current_index ?? 0;
  return (
    <div className="mt-6 rounded-2xl border border-neutral-200 bg-white p-4 sm:p-5" data-testid="founder-progress-stepper">
      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-neutral-500">
        Your capital raising progress
      </p>
      <ol className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-0">
        {data.stages.map((s, i) => {
          const isCurrent = i === cur && !s.done;
          const last = i === data.stages.length - 1;
          return (
            <li key={s.key} className="relative flex flex-1 gap-3 sm:flex-col sm:gap-0" data-testid={`stepper-stage-${s.key}`}>
              <div className="flex items-center sm:w-full">
                <span className={`z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                  s.done ? "bg-[#0A8B85] text-white"
                    : isCurrent ? "border-2 border-[#F59E0B] bg-white text-[#F59E0B]"
                    : "border border-neutral-300 bg-white text-neutral-400"}`}>
                  {s.done ? <Check className="h-4 w-4" /> : i + 1}
                </span>
                {!last && (
                  <span className={`hidden h-0.5 flex-1 sm:block ${s.done ? "bg-[#0A8B85]" : "bg-neutral-200"}`} />
                )}
              </div>
              <div className="sm:mt-2 sm:pr-4">
                <p className={`text-xs font-bold leading-snug ${
                  s.done ? "text-neutral-400 line-through decoration-neutral-300"
                    : isCurrent ? "text-neutral-900" : "text-neutral-500"}`}>
                  {s.label}
                </p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {isCurrent && (
                    <span className="inline-flex items-center rounded-full bg-[#F59E0B]/15 px-2 py-0.5 text-[9px] font-bold uppercase text-[#B45309]"
                      data-testid="stepper-you-are-here">
                      You are here
                    </span>
                  )}
                  {s.detail && (
                    <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[9px] font-semibold text-neutral-600">{s.detail}</span>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export default function FounderDashboard() {
  const navigate = useNavigate();
  const { user: pathwayUser } = useAuth();
  const [comp, setComp] = useState(null);
  const [team, setTeam] = useState([]);
  const [docs, setDocs] = useState([]);
  const [interest, setInterest] = useState([]);
  const [editing, setEditing] = useState(false);
  const [completion, setCompletion] = useState(null);
  const [irs, setIrs] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [celebrateScore, setCelebrateScore] = useState(false);
  const [shareBannerDismissed, setShareBannerDismissed] = useState(false);
  // `loading` flips to false the moment the initial Promise.all resolves so
  // the dashboard can swap skeleton cards for real content. `analyzing`
  // surfaces a "we're crunching your numbers" state when the founder has
  // completed onboarding but the AI analysis hasn't written a score yet —
  // we poll /companies/me every 4 s until the score appears.
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);

  const load = useCallback(async () => {
    const [a, c, d, g, h, i] = await Promise.all([
      api.get("/companies/me"),
      api.get("/documents/me"),
      api.get("/companies/me/interest"),
      api.get("/companies/me/team").catch(() => ({ data: [] })),
      api.get("/companies/me/completion").catch(() => ({ data: null })),
      api.get("/companies/me/irs").catch(() => ({ data: { locked: true } })),
    ]);
    setComp(a.data);
    // Project ventures live on the PRS flow, not the business dashboard.
    if ((a.data?.kind || "business") === "project") {
      navigate(a.data?.prs_analysis ? "/project/report" : "/project/intake", { replace: true });
      return;
    }
    // Guided onboarding wizard (Jun 2026): the dashboard opens only once the
    // founder has downloaded their Capital Readiness Report. Until then they
    // continue the step-by-step flow on /onboarding.
    if (!a.data?.report_downloaded_at) {
      // Direct-path founders have no report to download — their home is the funder matches page.
      navigate(pathwayUser?.pathway === "direct" ? "/founder/apply" : "/onboarding", { replace: true });
      return;
    }
    setDocs(c.data); setInterest(d.data);
    setTeam(g.data || []);
    setCompletion(h.data);
    setIrs(i.data);
    setLoading(false);
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  // Lightweight score refresh — re-fetch the company doc (+ IRS) without
  // re-triggering the full skeleton load. Fired when a re-run completes
  // anywhere in the app (`clinq:analysis-completed`), and when the founder
  // comes back to this tab (focus / visibilitychange).
  const refreshScore = useCallback(async () => {
    try {
      const [a, i] = await Promise.all([
        api.get("/companies/me"),
        api.get("/companies/me/irs").catch(() => null),
      ]);
      if (a?.data && (a.data.kind || "business") === "business") setComp(a.data);
      if (i?.data) setIrs(i.data);
    } catch (_) { /* keep the current view */ }
  }, []);

  // Tracks whether the backend reports an in-flight analysis/re-run job so
  // the score poll below keeps running even though a score already exists.
  const [rerunActive, setRerunActive] = useState(false);
  const checkRerunJob = useCallback(async () => {
    try {
      const { data } = await api.get("/processing-status");
      setRerunActive(!!data?.analysis_active);
      return !!data?.analysis_active;
    } catch (_) { return false; }
  }, []);

  useEffect(() => {
    if (loading) return;
    const onDone = () => { refreshScore(); checkRerunJob(); };
    const onVisible = () => { if (document.visibilityState === "visible") onDone(); };
    window.addEventListener("clinq:analysis-completed", onDone);
    window.addEventListener("focus", onDone);
    document.addEventListener("visibilitychange", onVisible);
    checkRerunJob();
    return () => {
      window.removeEventListener("clinq:analysis-completed", onDone);
      window.removeEventListener("focus", onDone);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [loading, refreshScore, checkRerunJob]);

  // If the founder has completed onboarding but no AI score is back yet —
  // OR the backend reports an active re-run job — poll the company doc
  // every 4 s so the dashboard updates the moment the analysis finishes.
  // Auto-stops once the score lands / changes, the job ends, or after 2 min.
  useEffect(() => {
    if (loading || !comp) return;
    const scoreReady = (comp?.readiness_score || 0) > 0
                       || !!comp?.ai_analysis
                       || irs?.locked;
    if (scoreReady && !rerunActive) {
      setAnalyzing(false);
      return;
    }
    // Only poll if the founder has actually completed onboarding (otherwise
    // there's nothing to wait for — they need to upload docs first).
    const onboarded = !!comp?.snapshot_completed_at
                       || (completion?.completion_pct ?? 0) >= 80
                       || rerunActive;
    if (!onboarded) return;
    if (!scoreReady) setAnalyzing(true);
    const baseScore = comp?.readiness_score || 0;
    const baseAt = comp?.ai_analysis?.analyzed_at || null;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = scoreReady ? 45 : 30;  // 3 min for re-runs, 2 min first score
    const tick = async () => {
      attempts += 1;
      if (cancelled || attempts > maxAttempts) {
        setAnalyzing(false);
        setRerunActive(false);
        return;
      }
      try {
        const { data } = await api.get("/companies/me");
        if (cancelled) return;
        const scoreLanded = !scoreReady && ((data?.readiness_score || 0) > 0 || !!data?.ai_analysis);
        const scoreUpdated = scoreReady && ((data?.readiness_score || 0) !== baseScore
                                            || (data?.ai_analysis?.analyzed_at || null) !== baseAt);
        if (scoreLanded || scoreUpdated) {
          setComp(data);
          setAnalyzing(false);
          setRerunActive(false);
          refreshScore();
          return;
        }
        if (scoreReady) {
          // Re-run still cooking? Stop as soon as the backend says the job ended.
          const stillActive = await checkRerunJob();
          if (cancelled) return;
          if (!stillActive) { setComp(data); return; }
        } else {
          setComp(data);
        }
      } catch (_) { /* keep polling */ }
      setTimeout(tick, 4000);
    };
    const t = setTimeout(tick, 4000);
    return () => { cancelled = true; clearTimeout(t); };
  }, [loading, comp, irs, completion, rerunActive, refreshScore, checkRerunJob]);
  // First-time founders see the interactive product tour automatically
  // after the dashboard data has loaded.
  useEffect(() => {
    if (comp && completion) {
      maybeStartFounderTourOnFirstVisit();
    }
  }, [comp, completion]);

  // Celebrate the first time a founder sees their CCR score on the dashboard
  // (drives the highest-converting share moment).
  // NOTE: localStorage is used here strictly as a non-sensitive UX flag — it
  // only stores a boolean indicator that the celebration has been shown.
  // No PII or credentials are persisted. Using sessionStorage would
  // re-trigger the celebration on every new browser session.
  useEffect(() => {
    if (!comp) return;
    // Always read the canonical Capital Readiness Score from `comp.readiness_score`
    // — written by the AI-analysis pipeline and floored by the high-water mark.
    // The IRS endpoint mirrors the same value, but reading from `comp` here
    // guarantees the celebration trigger uses the SAME number rendered on
    // every other tab (report, outreach, PDF).
    const score = irs?.locked ? 0 : (comp?.readiness_score || irs?.readiness_score || 0);
    if (!score) return;
    // Overlay priority: on a founder's very first visit the product tour
    // runs — defer the score celebration to the next dashboard visit so
    // modals never stack (especially cramped on mobile).
    if (!localStorage.getItem("clinq-founder-tour-completed-v1")) return;
    const key = `clinq_first_score_celebrated_${comp.company_id}`;
    if (!localStorage.getItem(key)) {
      localStorage.setItem(key, "1");
      // Delay a beat so the dashboard finishes paint
      setTimeout(() => {
        setCelebrateScore(true);
        setShareOpen(true);
      }, 800);
    }
  }, [comp, irs]);

  const score = comp?.readiness_score || 0;

  // Pitch Deck / Financial Model coverage only applies to equity-style raises.
  // capital_purposes (quiz) takes precedence when present; raise_reasons
  // (wizard picker) is the fallback for founders without quiz purposes.
  const dashPurposes = Array.isArray(comp?.capital_purposes) ? comp.capital_purposes : [];
  const dashReasons = Array.isArray(comp?.raise_reasons) ? comp.raise_reasons : [];
  const showEquityCoverage = dashPurposes.length > 0
    ? dashPurposes.includes("growth_capital")
    : (dashReasons.includes("expansion") || dashReasons.includes("new_product"));

  // Requalification gate: business founders with no recorded capital purpose
  // are sent back through the onboarding wizard to classify their raise.
  const needsRequalify = !!comp
    && (comp.kind || "business") === "business"
    && dashPurposes.length === 0 && dashReasons.length === 0;
  useEffect(() => {
    if (needsRequalify) navigate("/onboarding", { replace: true });
  }, [needsRequalify, navigate]);
  const analysis = comp?.ai_analysis;

  // David welcome popup — every login (session) until permanently dismissed.
  const { user: authUser, refresh: refreshAuth } = useAuth();
  const [showDavid, setShowDavid] = useState(false);
  // Documents checklist moved out of the pre-sign-in quiz: ask post-revenue
  // founders once, on their first dashboard visit.
  const [docsChecklistDone, setDocsChecklistDone] = useState(false);
  const quiz = comp?.get_started_quiz;
  const needsDocsChecklist = !!quiz && quiz.stage === "post_revenue" && !quiz.docs_checklist_done && !(quiz.docs || []).length && !docsChecklistDone;
  useEffect(() => {
    if (!DAVID_ENABLED) return;
    if (!authUser || authUser.role !== "founder") return;
    if (authUser.david_popup_dismissed) return;
    if (sessionStorage.getItem("clinq_david_popup_seen")) return;
    const t = setTimeout(() => {
      // Never stack on top of the first-visit product tour.
      if (document.querySelector(".driver-overlay, .driver-active")) return;
      setShowDavid(true);
    }, 3000);
    return () => clearTimeout(t);
  }, [authUser]);
  const closeDavid = () => {
    try { sessionStorage.setItem("clinq_david_popup_seen", "1"); } catch (_) { /* ignore */ }
    setShowDavid(false);
  };
  const neverShowDavid = async () => {
    closeDavid();
    try { await api.post("/founder/david-popup/dismiss"); await refreshAuth?.(); } catch (_) { /* ignore */ }
  };
  const davidStartTour = () => {
    api.post("/founder/david-popup/event", { option: "tour" }).catch(() => {});
    closeDavid();
    setTimeout(startDavidWalkthrough, 250);
  };
  const davidOpenFaq = () => {
    api.post("/founder/david-popup/event", { option: "faq" }).catch(() => {});
    closeDavid();
    window.dispatchEvent(new CustomEvent("clinq:open-david", { detail: { faq: true } }));
  };
  const davidPickHuman = () => {
    api.post("/founder/david-popup/event", { option: "human" }).catch(() => {});
  };

  // Canonical CCR Score — same source the report/outreach/PDF use.
  // Falls back to the IRS-endpoint value only if `comp.readiness_score` is
  // somehow missing (defensive; shouldn't happen post-analysis).
  const displayedScore = comp?.readiness_score || irs?.readiness_score || 0;

  // Investment-Grade viral nudge — shown once a founder crosses the 70-point
  // CCR threshold ("Investment-Grade" band). One-tap re-share via the
  // personalised share card is our cheapest viral loop. Dismissal is sticky
  // per-company (so we don't nag) and re-triggers if the score recovers
  // after dropping below 70.
  const investmentGradeKey = comp?.company_id
    ? `clinq_ig_share_banner_dismissed_${comp.company_id}`
    : null;
  useEffect(() => {
    if (!investmentGradeKey) return;
    setShareBannerDismissed(localStorage.getItem(investmentGradeKey) === "1");
  }, [investmentGradeKey]);
  const showInvestmentGradeBanner =
    !!comp && !irs?.locked && displayedScore >= 70 && !shareBannerDismissed;
  const dismissShareBanner = () => {
    setShareBannerDismissed(true);
    if (investmentGradeKey) localStorage.setItem(investmentGradeKey, "1");
  };

  return (
    <AppShell nav={NAV}>
      {needsDocsChecklist && (
        <FirstVisitDocsChecklist initial={quiz.docs || []} onDone={() => { setDocsChecklistDone(true); load(); }} />
      )}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="min-w-0 break-words text-2xl font-bold text-neutral-900 sm:text-3xl">{comp?.company_name || "Your dashboard"}</h1>
            <VentureSwitcher />
            <button data-testid="edit-profile-btn" onClick={()=>setEditing(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50">
              <Pencil className="h-3 w-3"/> Edit
            </button>
          </div>
          <p className="mt-1 text-sm text-neutral-600">{comp?.sector} · {comp?.country} · {comp?.stage}</p>
        </div>
        <span className={"rounded-full px-3 py-1 text-xs font-semibold " +
          (comp?.status === "verified" ? "bg-[#0A8B85] text-white" : "bg-amber-100 text-amber-800")}>
          {comp?.status === "verified" ? "Verified" : "Onboarding"}
        </span>
      </div>

      {/* 5-stage capital raising progress stepper (after header block) */}
      <ProgressStepper />

      {/* Capital Purpose badges (CRS v2.2) — instant visibility into why the
          pillar weights are set the way they are */}
      {Array.isArray(comp?.capital_purposes) && comp.capital_purposes.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="dashboard-capital-purposes">
          <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-neutral-500">
            Capital purpose
          </span>
          {comp.capital_purposes.map((p) => (
            <span key={p} data-testid={`capital-purpose-badge-${p}`}
              className={"inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold " +
                (p === comp.capital_purpose_primary
                  ? "bg-[#0A8B85] text-white"
                  : "bg-[#E5EEF1] text-[#00676F]")}>
              {CAPITAL_PURPOSE_LABEL[p] || p}
              {p === comp.capital_purpose_primary && comp.capital_purposes.length > 1 && (
                <span className="ml-1 font-normal opacity-80">· primary</span>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Business snapshot card — surfaces auto-extracted sector + ticket +
          capital mix + financial forecast at a glance. Feb 2026 — replaces
          fields that used to live in the removed Onboarding Step 1. */}
      <DashboardBusinessSnapshot comp={comp} onUpdate={load} onAddReason={() => setEditing(true)} />

      {/* One-time capital-purpose prompt for post-revenue founders who
          finished the quiz before this question existed */}
      <CapitalPurposePrompt comp={comp} onSaved={load} />

      {/* Warm intros made by the founder's venture studio (hidden when none) */}
      <StudioIntrosCard />

      {/* Figures Clinq read from uploaded documents — collapsible, founders
          can correct extraction errors inline (feeds Section 7B). */}
      <FiguresPanel />

      {/* Top-action nudge — surfaces the highest-impact un-done Capital
          Readiness checklist item so founders always know exactly what
          to do next. Drives weekly return visits and keeps founders
          climbing toward funder-ready. Hidden once the checklist is
          fully ticked. */}
      <TopActionThisWeek comp={comp} onChange={load} />

      {/* Profile prompt — checklist can't populate until the founder has
          classified their raise (capital purpose + target). */}
      {completion?.needs_profile && (
        <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid="completion-needs-profile">
          <p className="text-sm font-bold text-amber-900">Complete your profile to see your document checklist</p>
          <p className="mt-1 text-xs text-amber-800">
            Tell us what you&apos;re raising capital for and your target raise — we&apos;ll then show
            only the documents funders for your capital type actually ask for.
          </p>
          <Link to="/onboarding" data-testid="completion-needs-profile-cta"
            className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-[#0A8B85] px-4 py-2 text-xs font-bold text-white hover:bg-[#066667]">
            Complete my profile <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      )}

      {/* Completion checklist (only show if not complete) */}
      {completion && !completion.needs_profile && !completion.all_complete && (
        <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid="completion-banner">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-bold text-amber-900">
                {completion.lite_path
                  ? "Pre-seed pathway — quick onboarding"
                  : "Complete your dashboard to see your Capital Readiness Score & matches"}
              </p>
              <p className="mt-0.5 text-xs text-amber-800">
                {completion.completed_steps}/{completion.total_steps} done · {completion.pct}% complete
              </p>
            </div>
            <div className="w-full sm:w-64">
              <div className="h-2 rounded-full bg-amber-200">
                <div className="h-2 rounded-full bg-amber-700 transition-all" style={{ width: `${completion.pct}%` }}/>
              </div>
            </div>
          </div>
          <ul className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {(completion.required_keys || []).map(key => {
              const meta = completion.labels?.[key]
                ? { label: completion.labels[key], to: "/onboarding?step=2" }
                : {
                nda_accepted: { label: "Accept NDA", to: "/onboarding" },
                role_set: { label: "Set your role in business", to: "/onboarding?focus=role" },
                revenue_stage_set: { label: "Confirm revenue stage", to: "/onboarding?focus=revenue" },
                urls_provided: { label: "Add website / LinkedIn", to: "/onboarding?focus=urls" },
                snapshot_complete: { label: "Complete company snapshot", to: "/onboarding" },
                pitch_deck_uploaded: { label: "Upload pitch deck", to: "/onboarding?step=2" },
                financial_model_uploaded: { label: "Upload financial model", to: "/onboarding?step=2" },
                mgmt_accounts_uploaded: { label: "Upload management accounts", to: "/onboarding?step=2" },
                legal_docs_uploaded: { label: "Upload company registration / legal docs", to: "/onboarding?step=2" },
              }[key] || { label: key, to: "/onboarding" };
              const done = completion.checks?.[key];
              return (
                <li key={key} data-testid={`check-${key}`}>
                  {done ? (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-green-500 text-[10px] font-bold text-white">✓</span>
                      <span className="text-neutral-500 line-through">{meta.label}</span>
                    </div>
                  ) : (
                    <Link
                      to={meta.to}
                      data-testid={`check-action-${key}`}
                      className="group flex items-center justify-between gap-2 rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm hover:border-amber-400 hover:bg-amber-50/60"
                    >
                      <span className="flex items-center gap-2">
                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-amber-200 text-[10px] font-bold text-amber-700">•</span>
                        <span className="text-neutral-900">{meta.label}</span>
                      </span>
                      <span className="text-xs font-semibold text-amber-700 opacity-80 group-hover:opacity-100">Add →</span>
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
          <div className="mt-3 flex gap-2">
            <Link to="/onboarding" className="text-xs font-semibold text-amber-900 hover:underline" data-testid="completion-go-onboarding">Edit snapshot →</Link>
            <LaunchPrepButton
              testid="completion-go-prep"
              prefillCompany={comp?.company_name || ""}
              prefillSector={comp?.sector || ""}
              prefillStage={comp?.revenue_stage || ""}
              prefillMissingDocs={["pitch_deck", "financial_model"].filter((d) => !(completion?.checks?.[`doc_${d}`] ?? completion?.checks?.[`${d}_uploaded`]))}
              className="text-xs font-semibold text-amber-900 hover:underline"
            >No deck/model? Build them free on Clinq Prep →</LaunchPrepButton>
          </div>
        </div>
      )}

      {comp?.launch_promo_granted_at && (
        <div
          data-testid="launch-promo-banner"
          className="mt-6 overflow-hidden rounded-2xl border border-amber-300 bg-gradient-to-r from-amber-50 via-amber-50 to-orange-50 p-5 shadow-sm"
        >
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-200 text-amber-700">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-amber-700">
                Launch offer · You&apos;re founder #{comp.launch_promo_slot ?? "?"} of 15
              </p>
              <p className="mt-1 text-base font-extrabold leading-snug text-neutral-900 sm:text-lg">
                Your Capital Readiness Report is on us 🎉
              </p>
              <p className="mt-1 text-xs leading-relaxed text-neutral-700 sm:text-[13px]">
                As one of our first 15 founders, you&apos;ve been gifted{" "}
                <span className="font-bold text-amber-800">your Capital Readiness Report + 3 free re-runs</span>
                {" "}— no payment required. Update your snapshot, re-run your analysis, and watch your Capital Readiness Score climb.
              </p>
            </div>
          </div>
        </div>
      )}

      {showInvestmentGradeBanner && (
        <div
          data-testid="investment-grade-share-banner"
          className="mt-6 overflow-hidden rounded-2xl border border-[#0A8B85]/30 bg-gradient-to-br from-[#0A8B85] via-[#066667] to-[#04302E] p-5 text-white shadow-md"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-400/20 text-amber-300">
                <Trophy className="h-5 w-5" />
              </div>
              <div>
                <p className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-amber-300">
                  Investment-Grade · {displayedScore}/100
                </p>
                <p className="mt-1 text-base font-extrabold leading-snug sm:text-lg">
                  You&apos;re funder-ready. Show the world.
                </p>
                <p className="mt-1 text-xs leading-relaxed text-white/80 sm:text-[13px]">
                  Share your Capital Readiness Score with a personalised card —
                  every founder who signs up from your link earns you{" "}
                  <span className="font-bold text-amber-200">+1 re-analysis credit</span>.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setCelebrateScore(false); setShareOpen(true); }}
                data-testid="investment-grade-share-cta"
                className="inline-flex items-center gap-1.5 rounded-lg bg-amber-400 px-4 py-2 text-xs font-bold text-[#04302E] shadow hover:bg-amber-300"
              >
                <Share2 className="h-3.5 w-3.5" /> Share my score
              </button>
              <button
                onClick={dismissShareBanner}
                data-testid="investment-grade-share-dismiss"
                aria-label="Dismiss"
                className="rounded-md p-1.5 text-white/60 hover:bg-white/10 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 lg:items-stretch">
        {/* Left column: Score + Team stacked so they sit beside the taller
            coverage panel — eliminates the whitespace that used to appear
            under Score / Team on laptop view. Team stretches to fill any
            remaining height. */}
        <div className="lg:col-span-1 flex flex-col gap-5 h-full">
        {/* Score card */}
        <div className="rounded-2xl border border-neutral-200 bg-white p-6" data-testid="score-card">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Capital Readiness</p>
            {!irs?.locked && displayedScore > 0 && (
              <button data-testid="score-card-share-btn"
                onClick={() => { setCelebrateScore(false); setShareOpen(true); }}
                className="inline-flex items-center gap-1 rounded-md border border-[#0A8B85]/30 bg-[#0A8B85]/5 px-2 py-1 text-[11px] font-bold text-[#066667] hover:bg-[#0A8B85]/10">
                <Share2 className="h-3 w-3" /> Share my score
              </button>
            )}
          </div>
          {loading ? (
            <div data-testid="score-card-skeleton" className="mt-3 space-y-3" aria-busy="true">
              <div className="h-10 w-32 animate-pulse rounded-lg bg-neutral-100" />
              {/* Indeterminate moving loading bar */}
              <div
                data-testid="score-card-loading-bar"
                className="h-2 w-full overflow-hidden rounded-full bg-neutral-100"
                role="progressbar"
                aria-label="Loading your Capital Readiness Score"
              >
                <div
                  className="h-2 w-1/3 rounded-full bg-gradient-to-r from-neutral-200 via-[#0A8B85]/70 to-neutral-200"
                  style={{ animation: "scoreShimmer 1.4s ease-in-out infinite" }}
                />
              </div>
              <p className="text-xs text-neutral-400">Loading your Capital Readiness Score…</p>
              <style>{`@keyframes scoreShimmer { 0%{transform:translateX(-100%)} 100%{transform:translateX(400%)} }`}</style>
            </div>
          ) : analyzing ? (
            <div data-testid="score-card-analyzing" className="mt-3" aria-busy="true">
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-extrabold text-[#0A8B85]/40">—</span>
                <span className="text-sm text-neutral-500">/ 100</span>
              </div>
              {/* Indeterminate teal "thinking" bar — matches the score-bar height */}
              <div className="mt-4 h-2 overflow-hidden rounded-full bg-neutral-100">
                <div
                  className="h-2 w-1/3 rounded-full bg-gradient-to-r from-[#0A8B85]/40 via-[#0A8B85] to-[#0A8B85]/40"
                  style={{ animation: "scoreShimmer 1.4s ease-in-out infinite" }}
                />
              </div>
              <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#0A8B85]">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Analyzing your business — usually 30–60 seconds.
              </p>
              <p className="mt-1 text-[11px] text-neutral-500">
                We refresh this page automatically as soon as your Capital Readiness Score is ready.
              </p>
              <style>{`@keyframes scoreShimmer { 0%{transform:translateX(-100%)} 100%{transform:translateX(400%)} }`}</style>
            </div>
          ) : irs?.locked ? (
            <>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-5xl font-extrabold text-neutral-300">—</span>
                <span className="text-sm text-neutral-500">/ 100</span>
              </div>
              <p className="mt-3 text-xs text-amber-700">🔒 {irs.message}</p>
              {(comp?.report_downloaded_at || comp?.readiness_report_unlocked) && (
                <>
                  <Link
                    to="/founder/readiness-report"
                    data-testid="dashboard-view-readiness-report"
                    className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#0A8B85] px-3 py-2 text-xs font-bold text-white transition-colors hover:bg-[#066667]"
                  >
                    <FileText className="h-3.5 w-3.5" />
                    View Capital Readiness Report
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                  <button
                    type="button"
                    data-testid="dashboard-download-report-pdf"
                    onClick={async () => {
                      try {
                        const res = await api.get("/founder/readiness/report.pdf", { responseType: "blob", timeout: 180000 });
                        const url = URL.createObjectURL(res.data);
                        const el = document.createElement("a");
                        el.href = url; el.download = "clinq-capital-readiness-report.pdf";
                        document.body.appendChild(el); el.click(); el.remove();
                        URL.revokeObjectURL(url);
                      } catch (_e) { /* report page has the fallback download */ }
                    }}
                    className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-[#0A8B85]/30 bg-white px-3 py-2 text-xs font-bold text-[#0A8B85] transition-colors hover:bg-[#F4F8F8]"
                  >
                    Download report PDF
                  </button>
                </>
              )}
            </>
          ) : (
            <>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-6xl font-extrabold text-[#0A8B85]" data-testid="dashboard-score">{displayedScore}</span>
                <span className="text-sm text-neutral-500">/ 100</span>
              </div>
              <div className="mt-4 h-2 rounded-full bg-neutral-100">
                <div className="h-2 rounded-full bg-[#0A8B85] transition-all" style={{ width: `${displayedScore}%` }}/>
              </div>
              {displayedScore > 0 && (
                <>
                  <Link
                    to="/founder/readiness-report"
                    data-testid="dashboard-view-readiness-report"
                    className="mt-5 inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#0A8B85] px-3 py-2 text-xs font-bold text-white transition-colors hover:bg-[#066667]"
                  >
                    <FileText className="h-3.5 w-3.5" />
                    View Capital Readiness Report
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                  {comp?.readiness_report_unlocked || comp?.report_downloaded_at ? (
                    <button
                      type="button"
                      data-testid="dashboard-download-report-pdf"
                      onClick={async () => {
                        try {
                          const res = await api.get("/founder/readiness/report.pdf", { responseType: "blob", timeout: 180000 });
                          const url = URL.createObjectURL(res.data);
                          const el = document.createElement("a");
                          el.href = url; el.download = "clinq-capital-readiness-report.pdf";
                          document.body.appendChild(el); el.click(); el.remove();
                          URL.revokeObjectURL(url);
                        } catch (_e) { /* report page has the fallback download */ }
                      }}
                      className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-[#0A8B85]/30 bg-white px-3 py-2 text-xs font-bold text-[#0A8B85] transition-colors hover:bg-[#F4F8F8]"
                    >
                      Download report PDF
                    </button>
                  ) : null}
                </>
              )}
            </>
          )}
        </div>

        {/* Team card */}
        <div className="rounded-2xl border border-neutral-200 bg-white p-6 flex-1" data-testid="team-card">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Team</p>
            <Link to="/founder/team" className="text-xs font-semibold text-[#0A8B85] hover:underline" data-testid="team-card-manage">
              Manage →
            </Link>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <Users className="h-5 w-5 text-[#0A8B85]"/>
            {loading ? (
              <span className="inline-block h-8 w-12 animate-pulse rounded-md bg-neutral-100" aria-busy="true" />
            ) : (
              <>
                <span className="text-3xl font-extrabold text-neutral-900">{team.length}</span>
                <span className="text-sm text-neutral-500">{team.length === 1 ? "member" : "members"}</span>
              </>
            )}
          </div>
          <ul className="mt-3 space-y-1.5">
            {team.slice(0, 4).map(m => (
              <li key={m.user_id} className="flex items-center gap-2 text-xs" data-testid={`dash-team-${m.user_id}`}>
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#0A8B85]/10 text-[10px] font-bold text-[#0A8B85]">
                  {(m.name || "?").charAt(0).toUpperCase()}
                </div>
                <span className="truncate text-neutral-700">{m.is_me ? "You" : m.name}</span>
                {m.is_primary && <Crown className="h-3 w-3 text-amber-500" title="Primary founder"/>}
                <span className="ml-auto text-neutral-500">{m.role}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Advisor booking card */}
        <div className="rounded-2xl border border-[#0A8B85]/25 bg-[#0A8B85]/5 p-6" data-testid="advisor-card">
          <div className="flex items-center gap-3">
            <img
              src="/david-avatar.jpg"
              alt="David, Clinq Relationship Manager"
              className="h-11 w-11 rounded-full border-2 border-[#0A8B85] object-cover"
            />
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-[#066667]">Talk to your Relationship Manager</p>
              <p className="text-sm font-semibold text-neutral-900">David · Clinq Relationship Manager</p>
            </div>
          </div>
          <p className="mt-3 text-xs leading-relaxed text-neutral-600">
            Get 1:1 help improving your Capital Readiness Score. Your first 30-minute session is free.
          </p>
          <Link
            to="/advisor"
            data-testid="advisor-card-book-btn"
            className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#0A8B85] px-3 py-2 text-xs font-bold text-white transition-colors hover:bg-[#066667]"
          >
            <CalendarClock className="h-3.5 w-3.5" />
            Book a Session
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        </div>{/* /Score+Team stack */}

        {/* Pitch Deck Coverage — equity raises only (growth/expansion capital) */}
        {showEquityCoverage && (
          <div className="lg:col-span-1" data-testid="pitch-deck-coverage-wrapper">
            <PitchDeckCoveragePanel />
          </div>
        )}

        {/* Financial Model Coverage — equity raises only (growth/expansion capital) */}
        {showEquityCoverage && (
          <div className="lg:col-span-1" data-testid="financial-coverage-wrapper">
            <FinancialCoveragePanel />
          </div>
        )}

        {/* Capital Structure (CRS v2.1) — shows only when v2.1 analysis is available */}
        <CapitalStructurePanel />

        {/* Cohort benchmark — hides silently if no peer data */}
        <BenchmarkPanel />

        {/* Analysis details */}
        {analysis && (
          <div className="rounded-2xl border border-neutral-200 bg-white p-6 lg:col-span-3" data-testid="analysis-card">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[#0A8B85]"/>
              <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Document Analysis</p>
            </div>
            <p className="mt-3 text-sm text-neutral-700">{analysis.summary}</p>
            <div className="mt-5 grid gap-5 md:grid-cols-3">
              <div>
                <p className="text-xs font-bold uppercase text-neutral-500">Strengths</p>
                <ul className="mt-2 space-y-1.5">{(analysis.strengths||[]).map((s,i)=><li key={`s-${i}-${(s||"").slice(0,40)}`} className="flex gap-2 text-sm"><span className="text-[#0A8B85]">✓</span>{s}</li>)}</ul>
              </div>
              <div>
                <p className="text-xs font-bold uppercase text-neutral-500">Weaknesses</p>
                <ul className="mt-2 space-y-1.5">{(analysis.weaknesses||[]).map((s,i)=><li key={`w-${i}-${(s||"").slice(0,40)}`} className="flex gap-2 text-sm"><span className="text-amber-600">!</span>{s}</li>)}</ul>
              </div>
              <div>
                <p className="text-xs font-bold uppercase text-neutral-500">Roadmap</p>
                <ol className="mt-2 space-y-1.5">{sortRoadmap(analysis.roadmap).map((r,i)=><li key={`r-${i}-${r?.timeframe || ""}`} className="text-sm"><span className="font-semibold text-[#0A8B85]">{r.timeframe}</span> — {r.milestone}</li>)}</ol>
              </div>
            </div>
          </div>
        )}

        {/* VDR */}
        <div className="rounded-2xl border border-neutral-200 bg-white p-6 lg:col-span-3" data-testid="vdr-card">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Data Room</p>
            <Link to="/onboarding" className="text-xs font-semibold text-[#0A8B85] hover:underline">Upload more</Link>
          </div>
          {docs.length === 0 ? (
            <p className="mt-4 text-sm text-neutral-500">No documents yet.</p>
          ) : (
            <ul className="mt-4 divide-y divide-neutral-100">
              {docs.map(d => (
                <li key={d.doc_id} className="flex items-center gap-3 py-3 text-sm">
                  <FileText className="h-4 w-4 text-[#0A8B85]"/>
                  <span className="flex-1 truncate">{d.filename}</span>
                  {d.uploaded_by_role === "rm" && (
                    <span className="rounded-full bg-[#0A8B85]/10 px-2 py-0.5 text-[10px] font-bold text-[#0A8B85]" data-testid={`doc-added-by-rm-${d.doc_id}`}>
                      Added by your RM{d.uploaded_by_name ? ` · ${d.uploaded_by_name}` : ""}
                    </span>
                  )}
                  <UploadFailedBadge doc={d} />
                  <span className="text-xs uppercase tracking-wider text-neutral-500">{String(d.doc_type || "").replace(/_/g, " ")}</span>
                  <span className="text-xs text-neutral-400">{(d.size/1024).toFixed(0)}KB</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <EditCompanyModal open={editing} onClose={()=>setEditing(false)} onSaved={load}/>
      <ShareScoreModal
        open={shareOpen}
        onClose={() => { setShareOpen(false); setCelebrateScore(false); }}
        score={irs?.locked ? 0 : displayedScore}
        companyName={comp?.company_name || "My company"}
        celebration={celebrateScore}
        anchor={celebrateScore ? "first-score" : "dashboard"}
      />
      {showDavid && (
        <DavidWelcomePopup
          firstName={(authUser?.name || "there").split(" ")[0]}
          onClose={closeDavid}
          onNever={neverShowDavid}
          onStartTour={davidStartTour}
          onOpenFaq={davidOpenFaq}
          onPickHuman={davidPickHuman}
        />
      )}
    </AppShell>
  );
}


/* -- Top-action nudge (Feb 2026) -------------------------------------------
 * Pulls the highest-impact un-done item from the founder's personalised
 * Capital Readiness checklist and renders it as a single, eye-catching
 * card. Goal: every dashboard visit answers "what should I do next?" in
 * one glance — no scrolling through the full roadmap.
 *
 * Hidden when:
 *   • the checklist hasn't been built yet (founder hasn't unlocked the
 *     detailed report),
 *   • every item is already ticked (great — celebrate, don't nag).
 *
 * "Mark done" hits the existing /founder/readiness/checklist/{id}/toggle
 * endpoint then refreshes the parent dashboard so the next un-done item
 * surfaces automatically. */
function TopActionThisWeek({ comp, onChange }) {
  const [busy, setBusy] = React.useState(false);
  const checklist = Array.isArray(comp?.readiness_checklist)
    ? comp.readiness_checklist
    : [];
  if (!checklist.length) return null;
  // First un-done item — checklist is already ordered by impact upstream
  // (map_weaknesses_to_checklist surfaces AI-flagged weaknesses first).
  const top = checklist.find((it) => !it.done);
  if (!top) return null;
  const remaining = checklist.filter((it) => !it.done).length;
  const total = checklist.length;
  const done = total - remaining;

  const markDone = async () => {
    setBusy(true);
    try {
      await api.post(
        `/founder/readiness/checklist/${encodeURIComponent(top.id)}/toggle`,
        { done: true },
      );
      toast.success("Nice — one less thing standing between you and funders.");
      await onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update that action");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="top-action-this-week"
      className="mt-6 overflow-hidden rounded-2xl border border-[#0A8B85]/20 bg-gradient-to-br from-[#0A8B85]/8 via-white to-amber-50/40 p-5 sm:p-6"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0A8B85]/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#0A8B85]">
              <Sparkles className="h-3 w-3" /> Your top action this week
            </span>
            <span
              data-testid="top-action-category"
              className="text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-500"
            >
              · {top.category || "Capital Readiness"}
            </span>
          </div>
          <h3
            data-testid="top-action-title"
            className="mt-2 text-xl font-extrabold leading-tight tracking-tight text-neutral-900"
          >
            {top.title}
          </h3>
          {top.detail && (
            <p className="mt-1.5 text-sm leading-relaxed text-neutral-700">
              {top.detail}
            </p>
          )}
          <p
            data-testid="top-action-progress"
            className="mt-3 text-xs font-semibold text-neutral-500"
          >
            {done}/{total} actions ticked
            <span className="ml-2 font-normal text-neutral-400">
              · {remaining} left to reach funder-ready
            </span>
          </p>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="top-action-mark-done"
          disabled={busy}
          onClick={markDone}
          className="inline-flex items-center gap-2 rounded-lg bg-[#0A8B85] px-4 py-2 text-sm font-bold text-white transition hover:bg-[#066667] disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : null}
          {busy ? "Saving…" : "Mark as done"}
        </button>
        <Link
          to="/founder/readiness-report"
          data-testid="top-action-see-all"
          className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-300 bg-white px-4 py-2 text-sm font-semibold text-neutral-700 transition hover:border-[#0A8B85] hover:text-[#0A8B85]"
        >
          See all actions <ArrowRight className="h-4 w-4"/>
        </Link>
      </div>
    </section>
  );
}


/* -- Auto-extracted business snapshot (Feb 2026: replaces removed Step 1) ---
 * Shown directly under the header. Pulls sector, target raise, capital mix
 * and FY2025/FY2026 revenue + EBITDA + margin from the company doc. Renders
 * nothing if the company is brand-new (no extracted fields yet) to avoid an
 * empty-state card cluttering the dashboard on day-one.
 *
 * When the AI flagged a field as low-confidence the card becomes clickable —
 * the founder gets an inline edit modal so they can correct the value in 10
 * seconds without re-running analysis. Saving patches /companies/me/snapshot
 * with just the single field plus the trimmed low_confidence_fields list. */
function DashboardBusinessSnapshot({ comp, onUpdate, onAddReason }) {
  const [editField, setEditField] = React.useState(null); // field id being edited
  if (!comp) return null;
  const sector = comp.sector || null;
  const tMin = Number(comp.ticket_min || 0);
  const tMax = Number(comp.ticket_max || 0);
  const mix = comp.capital_mix || null;
  const mixEntries = mix
    ? Object.entries(mix).filter(([, v]) => Number(v) > 0)
    : [];
  const hasFinancials = comp.revenue_fy2025 != null || comp.revenue_fy2026 != null
                     || comp.ebitda_fy2025 != null  || comp.ebitda_fy2026 != null;
  const lowFields = Array.isArray(comp.low_confidence_fields)
    ? comp.low_confidence_fields
    : [];
  const _fmt = (n) => {
    if (n == null || n === "") return "—";
    const v = Number(n);
    if (!Number.isFinite(v) || v === 0) return "—";
    const abs = Math.abs(v);
    if (abs >= 1_000_000) return `${v < 0 ? "-" : ""}$${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
    if (abs >= 1_000) return `${v < 0 ? "-" : ""}$${(abs / 1_000).toFixed(0)}K`;
    return `${v < 0 ? "-" : ""}$${abs.toFixed(0)}`;
  };
  const _pct = (e, r) => {
    if (e == null || r == null) return null;
    const rn = Number(r);
    if (!Number.isFinite(rn) || rn === 0) return null;
    return ((Number(e) / rn) * 100).toFixed(1);
  };
  // Hide the card if there's literally nothing to show beyond defaults.
  if (!sector && tMin === 0 && tMax === 0 && mixEntries.length === 0 && !hasFinancials) {
    return null;
  }
  // A cell is editable when the AI flagged its field as low-confidence.
  // For the "Target raise" cell we look at ticket_max (the primary number).
  const _editable = (fieldId) => lowFields.includes(fieldId);
  return (
    <div
      data-testid="dashboard-business-snapshot"
      className="mt-5 rounded-2xl border border-neutral-200 bg-white p-5"
    >
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-500">
          Business snapshot
        </p>
        {(comp.extraction_confidence != null || lowFields.length > 0) ? (
          <ConfidenceRibbon
            confidence={comp.extraction_confidence}
            lowFields={lowFields}
            testid="dash-snap-confidence"
          />
        ) : (
          <p className="text-[11px] text-neutral-400">Auto-extracted from your uploads</p>
        )}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <SnapCell
          label="Sector"
          value={sector || "—"}
          testid="dash-snap-sector"
          flagged={_editable("sector")}
          onEdit={() => setEditField("sector")}
        />
        <SnapCell
          label="Target raise"
          value={(tMin > 0 || tMax > 0) ? `${_fmt(tMin)} – ${_fmt(tMax)}` : "—"}
          testid="dash-snap-ticket"
          flagged={_editable("ticket_max") || _editable("ticket_min")}
          onEdit={() => setEditField("ticket")}
        />
        <SnapCell
          label="FY2025 revenue"
          value={_fmt(comp.revenue_fy2025)}
          sub={(() => { const p = _pct(comp.ebitda_fy2025, comp.revenue_fy2025); return p != null ? `EBITDA ${p}%` : null; })()}
          testid="dash-snap-fy25"
          flagged={_editable("revenue_fy2025") || _editable("ebitda_fy2025")}
          onEdit={() => setEditField("fy2025")}
        />
        <SnapCell
          label="FY2026 revenue"
          value={_fmt(comp.revenue_fy2026)}
          sub={(() => { const p = _pct(comp.ebitda_fy2026, comp.revenue_fy2026); return p != null ? `EBITDA ${p}%` : null; })()}
          testid="dash-snap-fy26"
          flagged={_editable("revenue_fy2026") || _editable("ebitda_fy2026")}
          onEdit={() => setEditField("fy2026")}
        />
      </div>
      {mixEntries.length > 0 && (
        <div data-testid="dash-snap-capital-mix"
             className={"mt-3 flex flex-wrap items-center gap-2 border-t border-neutral-100 pt-3 " +
                        (_editable("capital_mix") ? "cursor-pointer hover:bg-amber-50/40 rounded-md -mx-2 px-2" : "")}
             onClick={_editable("capital_mix") ? () => setEditField("capital_mix") : undefined}
             title={_editable("capital_mix") ? "We were unsure — click to correct" : undefined}>
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#0A8B85]">
            Target capital mix{_editable("capital_mix") ? " · review" : ""}
          </span>
          {mixEntries.map(([k, v]) => (
            <span key={k} className={"rounded-full px-2.5 py-1 text-[11px] font-semibold " +
                                       (_editable("capital_mix")
                                         ? "bg-amber-100 text-amber-800 ring-1 ring-amber-200"
                                         : "bg-neutral-100 text-neutral-700")}>
              <span className="capitalize">{k}</span> · {v}%
            </span>
          ))}
        </div>
      )}
      {/* Why raising capital (Jun 2026) */}
      {(comp.raise_reasons || []).length > 0 ? (
        <div data-testid="dash-snap-raise-purpose"
             className="mt-3 border-t border-neutral-100 pt-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-widest text-[#0A8B85]">
              Raising capital for
            </span>
            {comp.raise_reasons.map((code, i) => {
              const meta = RAISE_REASONS.find((r) => r.code === code);
              const label = code === "other" && comp.raise_reason_other
                ? comp.raise_reason_other
                : (meta?.label || code);
              return (
                <span key={code}
                      data-testid={`dash-snap-raise-chip-${code}`}
                      className={"rounded-full px-2.5 py-1 text-[11px] font-semibold " +
                        (i === 0
                          ? "bg-[#0A8B85] text-white"
                          : "bg-[#0A8B85]/10 text-[#066667]")}>
                  {label}
                </span>
              );
            })}
          </div>
          {(comp.use_of_funds || "").trim() && (
            <p data-testid="dash-snap-use-of-funds"
               className="mt-1.5 truncate text-xs text-neutral-500">
              {comp.use_of_funds}
            </p>
          )}
        </div>
      ) : (
        <div data-testid="dash-snap-raise-purpose-empty"
             className="mt-3 flex flex-wrap items-center gap-2 border-t border-neutral-100 pt-3">
          <span className="text-[10px] font-bold uppercase tracking-widest text-neutral-400">
            Why are you raising capital?
          </span>
          <button
            data-testid="dash-snap-add-reason-btn"
            onClick={onAddReason}
            className="rounded-full border border-[#0A8B85]/40 bg-[#0A8B85]/5 px-2.5 py-1 text-[11px] font-bold text-[#066667] transition-colors hover:bg-[#0A8B85]/10">
            + Add your reason
          </button>
        </div>
      )}
      {editField && (
        <EditFieldModal
          field={editField}
          comp={comp}
          lowFields={lowFields}
          onClose={() => setEditField(null)}
          onSaved={async () => { setEditField(null); await onUpdate?.(); }}
        />
      )}
    </div>
  );
}

function SnapCell({ label, value, sub, testid, flagged, onEdit }) {
  const clickable = flagged && typeof onEdit === "function";
  return (
    <div
      data-testid={testid}
      onClick={clickable ? onEdit : undefined}
      title={clickable ? "We were unsure — click to correct" : undefined}
      className={
        "rounded-xl border px-3 py-2 relative transition " +
        (flagged
          ? "border-amber-300 bg-amber-50/30 ring-1 ring-amber-200 cursor-pointer hover:bg-amber-50"
          : "border-neutral-100 bg-neutral-50/40")
      }
    >
      <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
        {label}{flagged ? " · review" : ""}
      </p>
      <p className="mt-0.5 text-sm font-extrabold text-neutral-900">{value}</p>
      {sub && <p className="text-[10px] font-semibold text-[#0A8B85]">{sub}</p>}
      {flagged && (
        <span aria-hidden className="absolute right-2 top-2 text-[10px] text-amber-600">✎</span>
      )}
    </div>
  );
}


/* --- Inline edit modal for a single auto-extracted snapshot field ---
 * Patches /companies/me/snapshot with just the changed field PLUS a trimmed
 * low_confidence_fields list (the founder has now verified it, so it should
 * leave the "review" set). Backwards-compatible: posts the full required
 * SnapshotBody fields by spreading the current comp so the endpoint accepts
 * the partial update. */
function EditFieldModal({ field, comp, lowFields, onClose, onSaved }) {
  const sectorOptions = [
    "Fintech", "Agritech", "Healthtech", "Cleantech", "Logistics",
    "E-commerce", "Manufacturing", "Edtech", "Proptech", "Insurtech",
    "Hospitality", "Foodtech", "Real Estate", "Coworking", "Other",
  ];
  const initial = React.useMemo(() => {
    switch (field) {
      case "sector":      return { sector: comp.sector || "" };
      case "ticket":      return { ticket_min: comp.ticket_min || 0, ticket_max: comp.ticket_max || 0 };
      case "fy2025":      return { revenue_fy2025: comp.revenue_fy2025 ?? "", ebitda_fy2025: comp.ebitda_fy2025 ?? "" };
      case "fy2026":      return { revenue_fy2026: comp.revenue_fy2026 ?? "", ebitda_fy2026: comp.ebitda_fy2026 ?? "" };
      case "capital_mix": return { capital_mix: { ...(comp.capital_mix || { equity: 0, debt: 0, grant: 0, other: 0 }) } };
      default:            return {};
    }
  }, [field, comp]);
  const [form, setForm] = React.useState(initial);
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState("");

  // Which low_confidence_fields entries this edit clears once saved.
  const FIELD_GROUPS = {
    sector:      ["sector"],
    ticket:      ["ticket_min", "ticket_max"],
    fy2025:      ["revenue_fy2025", "ebitda_fy2025"],
    fy2026:      ["revenue_fy2026", "ebitda_fy2026"],
    capital_mix: ["capital_mix"],
  };

  const titles = {
    sector: "Sector",
    ticket: "Target capital raise",
    fy2025: "FY2025 forecast",
    fy2026: "FY2026 forecast",
    capital_mix: "Target capital mix",
  };

  const _num = (v) => (v === "" || v == null ? null : Number(v));

  const save = async () => {
    setSaving(true); setErr("");
    try {
      // Spread the current comp to satisfy SnapshotBody required-field validation.
      const body = {
        company_name: comp.company_name || "",
        website: comp.website || "",
        sector: comp.sector || "",
        country: comp.country || "",
        stage: comp.stage || "",
        ticket_min: Number(comp.ticket_min || 0),
        ticket_max: Number(comp.ticket_max || 0),
        value_prop: comp.value_prop || "",
        revenue_annual: Number(comp.revenue_annual || 0),
        employees: Number(comp.employees || 1),
        sdgs: comp.sdgs || [],
        // Carry forward all auto-extracted fields so we don't wipe them.
        legal_name: comp.legal_name || null,
        registration_number: comp.registration_number || null,
        director_name: comp.director_name || null,
        revenue_fy2025: comp.revenue_fy2025 ?? null,
        revenue_fy2026: comp.revenue_fy2026 ?? null,
        ebitda_fy2025: comp.ebitda_fy2025 ?? null,
        ebitda_fy2026: comp.ebitda_fy2026 ?? null,
        capital_mix: comp.capital_mix || null,
      };
      // Apply the edit on top.
      if (field === "sector")      body.sector = form.sector || comp.sector;
      if (field === "ticket")      { body.ticket_min = Number(form.ticket_min || 0); body.ticket_max = Number(form.ticket_max || 0); }
      if (field === "fy2025")      { body.revenue_fy2025 = _num(form.revenue_fy2025); body.ebitda_fy2025 = _num(form.ebitda_fy2025); }
      if (field === "fy2026")      { body.revenue_fy2026 = _num(form.revenue_fy2026); body.ebitda_fy2026 = _num(form.ebitda_fy2026); }
      if (field === "capital_mix") body.capital_mix = form.capital_mix;
      // Trim the cleared fields out of low_confidence_fields.
      const cleared = new Set(FIELD_GROUPS[field] || []);
      body.low_confidence_fields = lowFields.filter((k) => !cleared.has(k));
      body.extraction_confidence = comp.extraction_confidence ?? null;
      await api.post("/companies/me/snapshot", body);
      toast.success("Saved.");
      await onSaved?.();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="dash-snap-edit-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-600">
          We were unsure · review &amp; save
        </p>
        <h3 className="mt-1 text-xl font-extrabold text-neutral-900">{titles[field]}</h3>

        {field === "sector" && (
          <div className="mt-4">
            <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500">Sector</label>
            <select
              data-testid="edit-sector-select"
              value={form.sector}
              onChange={(e) => setForm({ ...form, sector: e.target.value })}
              className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm"
            >
              <option value="">Select…</option>
              {sectorOptions.map((s) => <option key={s} value={s}>{s}</option>)}
              {comp.sector && !sectorOptions.includes(comp.sector) && (
                <option value={comp.sector}>{comp.sector} (current)</option>
              )}
            </select>
          </div>
        )}

        {field === "ticket" && (
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500">Min (USD)</label>
              <input
                data-testid="edit-ticket-min"
                type="number" min="0" step="1000"
                value={form.ticket_min}
                onChange={(e) => setForm({ ...form, ticket_min: e.target.value })}
                className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500">Max (USD)</label>
              <input
                data-testid="edit-ticket-max"
                type="number" min="0" step="1000"
                value={form.ticket_max}
                onChange={(e) => setForm({ ...form, ticket_max: e.target.value })}
                className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm"
              />
            </div>
          </div>
        )}

        {(field === "fy2025" || field === "fy2026") && (
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500">Revenue (USD)</label>
              <input
                data-testid={`edit-${field}-revenue`}
                type="number" step="1000"
                value={form[`revenue_${field}`]}
                onChange={(e) => setForm({ ...form, [`revenue_${field}`]: e.target.value })}
                className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500">Op. profit / EBITDA</label>
              <input
                data-testid={`edit-${field}-ebitda`}
                type="number" step="1000"
                value={form[`ebitda_${field}`]}
                onChange={(e) => setForm({ ...form, [`ebitda_${field}`]: e.target.value })}
                className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm"
              />
            </div>
            <p className="col-span-2 text-[11px] text-neutral-500">
              Use a negative number for loss-making years (e.g. <code>-180000</code>).
            </p>
          </div>
        )}

        {field === "capital_mix" && (
          <div className="mt-4 space-y-2">
            {["equity", "debt", "grant", "other"].map((k) => (
              <div key={k} className="flex items-center gap-3">
                <label className="w-20 text-xs font-bold uppercase tracking-wider text-neutral-500 capitalize">{k}</label>
                <input
                  data-testid={`edit-mix-${k}`}
                  type="number" min="0" max="100" step="1"
                  value={form.capital_mix?.[k] ?? 0}
                  onChange={(e) => setForm({
                    ...form,
                    capital_mix: { ...form.capital_mix, [k]: Math.max(0, Math.min(100, Number(e.target.value || 0))) },
                  })}
                  className="w-24 rounded-lg border border-neutral-300 px-3 py-1.5 text-sm"
                />
                <span className="text-xs text-neutral-500">%</span>
              </div>
            ))}
            <p className="text-[11px] text-neutral-500">
              Total: {Object.values(form.capital_mix || {}).reduce((a, b) => a + Number(b || 0), 0)}%
              {Object.values(form.capital_mix || {}).reduce((a, b) => a + Number(b || 0), 0) > 100 && (
                <span className="ml-1 font-bold text-rose-600">— above 100%</span>
              )}
            </p>
          </div>
        )}

        {err && <p className="mt-3 rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-700">{err}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button
            data-testid="edit-modal-cancel"
            type="button"
            onClick={onClose}
            className="rounded-lg border border-neutral-300 px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50"
          >Cancel</button>
          <button
            data-testid="edit-modal-save"
            type="button"
            disabled={saving}
            onClick={save}
            className="rounded-lg bg-[#0A8B85] px-4 py-2 text-sm font-bold text-white hover:bg-[#087570] disabled:opacity-60"
          >{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}
